import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * State for the drafting feature. A draft is an artifact version (today, the latest COMPLETE
 * `md` version of a matter artifact). Version records remain in `matterArtifactsState`; this slice
 * indexes the current draft heads for matter-level views.
 */
export type DraftingState = {
  /* The conversation used to reopen each draft. */
  conversationIdByDraftArtifactVersionId: Partial<{ [draftArtifactVersionId: string]: string }>
  matterArtifactDraftVersionIdsByMatterId: Partial<{ [matterId: string]: string[] }>
  matterArtifactDraftsLastRefreshByMatterId: Partial<{ [matterId: string]: number }>
  matterArtifactDraftsLoadingByMatterId: Partial<{ [matterId: string]: boolean }>
  matterArtifactDraftsErrorByMatterId: Partial<{ [matterId: string]: boolean }>
}

const initialState: DraftingState = {
  conversationIdByDraftArtifactVersionId: {},
  matterArtifactDraftVersionIdsByMatterId: {},
  matterArtifactDraftsLastRefreshByMatterId: {},
  matterArtifactDraftsLoadingByMatterId: {},
  matterArtifactDraftsErrorByMatterId: {},
}

/**
 * Marks a matter's draft list stale so the next refresh refetches it.
 */
export const kStaleDraftsTimestamp = 0

export const draftingSlice = createSlice({
  name: 'draftingState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterArtifactDrafts: (_state, _action: PayloadAction<{ matterId: string; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    setDraftArtifactVersionConversationIds: (state, action: PayloadAction<Partial<{ [draftArtifactVersionId: string]: string }>>) => {
      Object.assign(state.conversationIdByDraftArtifactVersionId, action.payload)
    },

    setMatterArtifactDraftVersionIdsForMatter: (state, action: PayloadAction<{ matterId: string; artifactVersionIds: string[] }>) => {
      state.matterArtifactDraftVersionIdsByMatterId[action.payload.matterId] = action.payload.artifactVersionIds
    },

    setMatterArtifactDraftsLoadingForMatter: (state, action: PayloadAction<{ matterId: string; loading: boolean }>) => {
      state.matterArtifactDraftsLoadingByMatterId[action.payload.matterId] = action.payload.loading
    },

    setMatterArtifactDraftsErrorForMatter: (state, action: PayloadAction<{ matterId: string; error: boolean }>) => {
      state.matterArtifactDraftsErrorByMatterId[action.payload.matterId] = action.payload.error
    },

    setMatterArtifactDraftsLastRefreshForMatter: (state, action: PayloadAction<{ matterId: string; timestamp: number }>) => {
      state.matterArtifactDraftsLastRefreshByMatterId[action.payload.matterId] = action.payload.timestamp
    },

    /**
     * Invalidates a matter's draft list so the next refresh refetches it.
     */
    invalidateMatterDrafts: (state, action: PayloadAction<{ matterId: string }>) => {
      const { matterId } = action.payload
      // A never-loaded matter has nothing cached to invalidate.
      if (state.matterArtifactDraftsLastRefreshByMatterId[matterId] !== undefined) {
        state.matterArtifactDraftsLastRefreshByMatterId[matterId] = kStaleDraftsTimestamp
      }
    },
  },
})

// Actions
export const DraftingActions = draftingSlice.actions

export default draftingSlice.reducer
